/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';

import { Menu } from './styles';

export type ImageProviderId = 'gemini' | 'openai';

interface ImageProvider {
  id: ImageProviderId;
  label: string;
  description: string;
}

const IMAGE_PROVIDERS: ImageProvider[] = [
  { id: 'gemini', label: 'Nano Banana', description: 'Gemini 2.5 Flash Image' },
  { id: 'openai', label: 'GPT', description: 'gpt-image-1' },
];

interface CentralIaComposerMenuProps {
  disabled?: boolean;
  webSearchActive: boolean;
  imageMode: ImageProviderId | null;
  onAttachFile: () => void;
  onToggleWebSearch: () => void;
  onSelectImageProvider: (provider: ImageProviderId | null) => void;
}

const CentralIaComposerMenu: React.FC<CentralIaComposerMenuProps> = ({
  disabled = false,
  webSearchActive,
  imageMode,
  onAttachFile,
  onToggleWebSearch,
  onSelectImageProvider,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageSubmenuOpen, setImageSubmenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setImageSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setImageSubmenuOpen(false);
  };

  return (
    <Menu ref={containerRef}>
      <button
        type="button"
        className="plus-button btn p-0 d-flex align-items-center justify-content-center"
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        title="Mais opções"
        aria-label="Mais opções"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <i className="material-icons">add</i>
      </button>

      {isOpen && (
        <div className="menu-popover">
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              onAttachFile();
            }}
          >
            <i className="material-icons">attach_file</i>
            <span className="label">Adicionar arquivos</span>
          </button>

          <button type="button" className="menu-item disabled" disabled title="Em breve">
            <i className="material-icons">schedule</i>
            <span className="label">Arquivos recentes</span>
            <i className="material-icons chevron">chevron_right</i>
          </button>

          <div className="divider" />

          <div
            className="submenu-wrapper"
            onMouseEnter={() => setImageSubmenuOpen(true)}
            onMouseLeave={() => setImageSubmenuOpen(false)}
          >
            <button
              type="button"
              className={`menu-item ${imageMode ? 'active' : ''}`}
              onClick={() => setImageSubmenuOpen((open) => !open)}
            >
              <i className="material-icons">image</i>
              <span className="label">Criar imagem</span>
              <i className="material-icons chevron">chevron_right</i>
            </button>

            {imageSubmenuOpen && (
              <div className="submenu-popover">
                {IMAGE_PROVIDERS.map((provider) => {
                  const isActive = imageMode === provider.id;
                  return (
                    <button
                      key={provider.id}
                      type="button"
                      className={`menu-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        onSelectImageProvider(isActive ? null : provider.id);
                        close();
                      }}
                    >
                      <span className="label">
                        <span className="provider-name">{provider.label}</span>
                        <span className="provider-desc">{provider.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            className={`menu-item ${webSearchActive ? 'active' : ''}`}
            onClick={() => {
              close();
              onToggleWebSearch();
            }}
          >
            <i className="material-icons">public</i>
            <span className="label">Busca na web</span>
            {webSearchActive && <span className="badge-on">ON</span>}
          </button>
        </div>
      )}
    </Menu>
  );
};

export default CentralIaComposerMenu;
